import React, { useState } from 'react';
import { Plus, Trash2, Edit2, FileText, Paperclip, Download, Folder, FileOutput } from 'lucide-react';
import { exportarPDF, exportarWord, htmlAnotacao, htmlReembolso, htmlRelatorioReembolsos, htmlNotaDebito } from '../utils/exportDoc';
import { toast } from '../utils/toast';
import { baixarAnexo } from '../utils/anexo';
import { supabase } from '../lib/supabase';
import { decryptData } from '../utils/encryption';

// Busca reembolsos frescos do Supabase — o state em memoria pode estar
// contaminado pelo placeholder '__b64__' do cache localStorage (que trunca
// base64 longos). A nota de debito precisa dos dados originais pra exibir
// as imagens dos comprovantes.
const buscarReembolsosFrescos = async () => {
  try {
    const { data, error } = await supabase
      .from('dados_compartilhados')
      .select('dados')
      .eq('id', 'sistema')
      .single();
    if (error || !data?.dados) return null;
    const decrypted = decryptData(data.dados);
    return decrypted?.reembolsos || null;
  } catch {
    return null;
  }
};

const lerArquivo = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (e) => resolve({ nome: file.name, dados: e.target.result });
  reader.readAsDataURL(file);
});

export const AnotacoesReembolsos = ({ anotacoes, setAnotacoes, reembolsos, setReembolsos, notasDebito, setNotasDebito, empresaSlugAtual, clientes, abaInicial, profissionalAtual, isAdmin, semTabs }) => {
  // Bianca 31/07 v2 (task 26): numeração de nota é POR CLIENTE
  // (Sadel/Magna/Valge...), não pela empresa da consultoria. Cada cliente
  // tem sua própria sequência anual (ND-YYYY-00001). O empresaSlug
  // (kzero-consultoria) é registrado só pra fins de escopo/compat com o
  // registro flat — mas o filtro que conta é clienteId.
  const SLUG_LEGADO = 'kzero-consultoria'
  const slugEmp = empresaSlugAtual || SLUG_LEGADO
  const [aba, setAba]   = useState(abaInicial || 'anotacoes');
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [filtroAnotacoes, setFiltroAnotacoes] = useState('');
  const [filtroReembolsos, setFiltroReembolsos] = useState('');
  const [filtroStatus, setFiltroStatus] = useState(''); // '' = todos | 'PENDENTE' | 'PAGO' | 'RECUSADO'
  // null = nível 1 (lista de pastas), string = nível 2 (anotações do cliente), 'sem-cliente' = sem clienteId
  const [pastaClienteId, setPastaClienteId] = useState(null);
  const [selecionados, setSelecionados] = useState([]);
  const [notaDebitoModal, setNotaDebitoModal] = useState(false);
  const [notaForm, setNotaForm] = useState({ numero:'', meioPagamento:'', dadosPagamento:'', vencimento:'', observacoes:'' });
  const [verHistoricoNotas, setVerHistoricoNotas] = useState(false);
  const [editandoNota, setEditandoNota] = useState(null); // id da nota sendo editada

  // ─── ANOTAÇÕES ──────────────────────────────────────────────────────────────
  const novaAnotacao  = (clienteIdFixo) => {
    setForm({ id: Date.now(), data: new Date().toISOString().split('T')[0], clienteId: clienteIdFixo !== undefined ? clienteIdFixo : '', titulo:'', conteudo:'', anexos:[], profissional: profissionalAtual||'', _clienteFixo: clienteIdFixo !== undefined });
    setEditId(null);
  };
  const editarAnotacao = (a) => { setForm({...a, _clienteFixo: false}); setEditId(a.id); };

  const salvarAnotacao = () => {
    if (!form.titulo) { toast.aviso('Preencha o título da anotação.'); return; }
    setAnotacoes(editId ? anotacoes.map(a => a.id === editId ? form : a) : [...anotacoes, form]);
    toast.sucesso(editId ? 'Anotação atualizada!' : 'Anotação salva!');
    setForm(null); setEditId(null);
  };

  const deletarAnotacao = (id) => { if (window.confirm('Excluir?')) { setAnotacoes(anotacoes.filter(a => a.id !== id)); toast.sucesso('Anotação excluída.'); } };

  const anotacoesBase = isAdmin ? anotacoes : anotacoes.filter(a => !a.profissional || a.profissional === profissionalAtual);
  const anotacoesFiltradas = filtroAnotacoes ? anotacoesBase.filter(a => String(a.clienteId) === filtroAnotacoes) : anotacoesBase;

  // ─── REEMBOLSOS ─────────────────────────────────────────────────────────────
const CATEGORIAS_REEMBOLSO = ['Transporte', 'Hospedagem', 'Alimentação', 'Material', 'Comunicação', 'Outros'];
  const novoReembolso   = () => { setForm({ id: Date.now(), data: new Date().toISOString().split('T')[0], clienteId:'', categoria:'', projeto:'', descricao:'', valor:'', status:'PENDENTE', comprovante:'', anexos:[], profissional: profissionalAtual||'' }); setEditId(null); };
  const editarReembolso = (r) => { setForm({...r}); setEditId(r.id); };

  const salvarReembolso = () => {
    if (!form.descricao || !form.valor) { toast.aviso('Preencha descrição e valor do reembolso.'); return; }
    setReembolsos(editId ? reembolsos.map(r => r.id === editId ? form : r) : [...reembolsos, form]);
    toast.sucesso(editId ? 'Reembolso atualizado!' : 'Reembolso salvo!');
    setForm(null); setEditId(null);
  };

  const deletarReembolso = (id) => { if (window.confirm('Excluir?')) { setReembolsos(reembolsos.filter(r => r.id !== id)); toast.sucesso('Reembolso excluído.'); } };

  const reembolsosBase = isAdmin ? reembolsos : reembolsos.filter(r => !r.profissional || r.profissional === profissionalAtual);
  const reembolsosFiltrados = reembolsosBase
    .filter(r => !filtroReembolsos || String(r.clienteId) === filtroReembolsos)
    .filter(r => !filtroStatus || (r.status || 'PENDENTE') === filtroStatus)
    // Bianca 31/07 (task 25): ORDEM CRESCENTE de data. Assim relatórios PDF/DOC
    // e nota de débito saem com itens em ordem cronológica. Reembolsos sem data
    // vão pro fim.
    .slice()
    .sort((a, b) => {
      if (!a.data && !b.data) return 0
      if (!a.data) return 1
      if (!b.data) return -1
      return a.data.localeCompare(b.data)
    });
  // Totais por status ignoram o filtroStatus pra cards sempre mostrarem o panorama completo;
  // respeitam só o filtro por cliente.
  const reembolsosParaTotais = filtroReembolsos
    ? reembolsosBase.filter(r => String(r.clienteId) === filtroReembolsos)
    : reembolsosBase;

  // Bianca 31/07 v2: histórico de notas segue o filtro por cliente (numeração
  // agora é por CLIENTE, não por empresa da consultoria). Sem filtro → mostra
  // todas.
  const notasVisiveis = filtroReembolsos
    ? (notasDebito || []).filter(n => String(n?.clienteId) === String(filtroReembolsos))
    : (notasDebito || []);

  const anexarArquivo = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const resultados = await Promise.all(files.map(lerArquivo));
    setForm(f => ({ ...f, anexos: [...(f.anexos || []), ...resultados] }));
    e.target.value = '';
  };

  const removerAnexo = (idx) => setForm(f => ({ ...f, anexos: (f.anexos||[]).filter((_, i) => i !== idx) }));

  const STATUS_COR = { PENDENTE:'#f59e0b', PAGO:'#10b981', RECUSADO:'#ef4444' };

  const totalPorStatus = (st) => reembolsosParaTotais.filter(r => (r.status||'PENDENTE') === st).reduce((s, r) => s + parseFloat(r.valor||0), 0);
  const countPorStatus = (st) => reembolsosParaTotais.filter(r => (r.status||'PENDENTE') === st).length;

  const toggleSelecionado = (id) => setSelecionados(sel => sel.includes(id) ? sel.filter(s => s !== id) : [...sel, id]);
  const toggleTodos = () => setSelecionados(sel => sel.length === reembolsosFiltrados.length ? [] : reembolsosFiltrados.map(r => r.id));
  const reembolsosSelecionados = reembolsosFiltrados.filter(r => selecionados.includes(r.id));
  const gerarCompilado = (tipo) => {
    if (!reembolsosSelecionados.length) return;
    if (tipo === 'pdf') exportarPDF(htmlRelatorioReembolsos(reembolsosSelecionados, clientes), 'Solicitação de Reembolsos');
    else exportarWord(htmlRelatorioReembolsos(reembolsosSelecionados, clientes), 'Solicitação de Reembolsos');
  };

  // Numeração sequencial POR CLIENTE (Sadel, Magna, Valge...). Cada cliente
  // tem sua própria sequência anual — Sadel próx = max(notas Sadel deste ano)+1.
  // Formato: ND-YYYY-00001 (5 dígitos zero-padded).
  // clienteId vem do primeiro reembolso selecionado (todos numa mesma nota
  // devem ser do mesmo cliente na prática).
  const gerarNumeroNota = (clienteIdArg) => {
    const clienteId = clienteIdArg ?? reembolsosSelecionados[0]?.clienteId
    const ano = new Date().getFullYear();
    const regex = new RegExp(`^ND-${ano}-(\\d+)$`);
    const notasDoCliente = (notasDebito || []).filter(n =>
      clienteId ? String(n?.clienteId) === String(clienteId) : !n?.clienteId
    )
    const ultimoSeq = notasDoCliente.reduce((max, n) => {
      const m = String(n?.numero || '').match(regex);
      return m ? Math.max(max, parseInt(m[1], 10)) : max;
    }, 0);
    return `ND-${ano}-${String(ultimoSeq + 1).padStart(5, '0')}`;
  };

  const abrirNotaDebito = () => {
    if (!reembolsosSelecionados.length) return;
    setNotaForm({ numero: gerarNumeroNota(), meioPagamento:'', dadosPagamento:'', vencimento:'', observacoes:'' });
    setNotaDebitoModal(true);
  };

  const gerarNotaDebito = (tipo) => {
    const numero = notaForm.numero || gerarNumeroNota();
    if (editandoNota) {
      // Modo edição: prefere o snapshot da nota (autossuficiente); só cai no
      // reencontro por ID nas notas legadas, com comparação tolerante a tipo.
      const notaOriginal = (notasDebito || []).find(n => n.id === editandoNota);
      const base = Array.isArray(notaOriginal?.itensSnapshot) && notaOriginal.itensSnapshot.length > 0
        ? notaOriginal.itensSnapshot
        : (notaOriginal?.itens || []).map(id => (reembolsos || []).find(r => String(r.id) === String(id))).filter(Boolean);
      const itens = base.map(r => ({ ...r, ...notaForm }));
      if (tipo === 'pdf') exportarPDF(htmlNotaDebito(itens, clientes, numero), 'Nota de Débito');
      else exportarWord(htmlNotaDebito(itens, clientes, numero), 'Nota de Débito');
      if (setNotasDebito) {
        // Updater funcional evita race se houver salvamento concorrente
        setNotasDebito(prev => (prev || []).map(n => n.id === editandoNota
          ? { ...n, numero, meioPagamento: notaForm.meioPagamento, dadosPagamento: notaForm.dadosPagamento, vencimento: notaForm.vencimento, observacoes: notaForm.observacoes }
          : n
        ));
        toast.sucesso(`Nota ${numero} atualizada.`);
      }
    } else {
      const itens = reembolsosSelecionados.map(r => ({ ...r, ...notaForm }));
      if (tipo === 'pdf') exportarPDF(htmlNotaDebito(itens, clientes, numero), 'Nota de Débito');
      else exportarWord(htmlNotaDebito(itens, clientes, numero), 'Nota de Débito');
      if (setNotasDebito) {
        const clienteId = reembolsosSelecionados[0]?.clienteId
        const registro = {
          id: String(Date.now()),
          numero,
          empresaSlug: slugEmp,
          clienteId,
          dataEmissao: new Date().toISOString().split('T')[0],
          cliente: clientes?.find(c => String(c.id) === String(clienteId))?.nome || '—',
          total: reembolsosSelecionados.reduce((s, r) => s + parseFloat(r.valor || 0), 0),
          itens: reembolsosSelecionados.map(r => r.id),
          // Snapshot dos itens DENTRO da nota — cópia completa, com anexos.
          // Antes a nota guardava só os IDs (`itens`) e reencontrava os
          // reembolsos na hora de emitir. Quando um overwrite entre abas apagava
          // esses reembolsos (ND-2026-00017, set/2026), a nota emitia vazia e
          // sem recuperação possível. Agora a nota é autossuficiente: emite
          // sempre igual ao momento em que foi criada, aconteça o que acontecer
          // com os lançamentos de origem.
          itensSnapshot: reembolsosSelecionados.map(r => ({ ...r })),
          meioPagamento: notaForm.meioPagamento,
          dadosPagamento: notaForm.dadosPagamento,
          vencimento: notaForm.vencimento,
          observacoes: notaForm.observacoes,
        };
        // Updater funcional evita race em emissões rápidas em sequência
        setNotasDebito(prev => [...(prev || []), registro]);
        toast.sucesso(`Nota ${numero} salva no histórico.`);
        setVerHistoricoNotas(true); // abre o histórico pra confirmação visual
      }
      setSelecionados([]);
    }
    setNotaDebitoModal(false);
    setEditandoNota(null);
  };

  const imprimirNota = async (n, tipo) => {
    let base;
    if (Array.isArray(n.itensSnapshot) && n.itensSnapshot.length > 0) {
      // Nota autossuficiente (criada após o fix): os itens vivem na própria
      // nota. Ainda assim tentamos enriquecer os anexos com a versão fresca do
      // banco quando o reembolso de origem ainda existe — o snapshot pode ter
      // '__b64__' truncado se foi salvo a partir do state em memória.
      const frescos = await buscarReembolsosFrescos();
      const fonte = Array.isArray(frescos) && frescos.length ? frescos : (reembolsos || []);
      base = n.itensSnapshot.map(item => {
        const fresco = fonte.find(r => String(r.id) === String(item.id));
        return fresco && (fresco.anexos || fresco.anexo) ? { ...item, anexos: fresco.anexos ?? item.anexos, anexo: fresco.anexo ?? item.anexo } : item;
      });
    } else {
      // Nota legada (só IDs). Reencontra por ID, com comparação tolerante a
      // tipo (number x string) — a causa de emitir vazio mesmo com os
      // reembolsos ainda no banco. Trata `frescos` vazio para não engolir o
      // fallback do state em memória.
      const frescos = await buscarReembolsosFrescos();
      const fonte = Array.isArray(frescos) && frescos.length ? frescos : (reembolsos || []);
      base = (n.itens || []).map(id => fonte.find(r => String(r.id) === String(id))).filter(Boolean);
    }
    const itens = base.map(r => ({ ...r, meioPagamento: n.meioPagamento, dadosPagamento: n.dadosPagamento, vencimento: n.vencimento, observacoes: n.observacoes }));
    if (itens.length === 0) {
      toast.erro(`Nota ${n.numero}: os lançamentos originais não foram encontrados. Reemita a partir dos reembolsos.`);
      return;
    }
    if (tipo === 'pdf') exportarPDF(htmlNotaDebito(itens, clientes, n.numero), 'Nota de Débito');
    else exportarWord(htmlNotaDebito(itens, clientes, n.numero), 'Nota de Débito');
  };

  const abrirEdicaoNota = (n) => {
    setEditandoNota(n.id);
    setNotaForm({ numero: n.numero, meioPagamento: n.meioPagamento || '', dadosPagamento: n.dadosPagamento || '', vencimento: n.vencimento || '', observacoes: n.observacoes || '' });
    setNotaDebitoModal(true);
  };

  const isAnotacoes = aba === 'anotacoes';

  return (
    <div className="m-container" style={s.container}>
      {!semTabs && <h2 style={s.titulo}>{isAnotacoes ? 'Anotações' : 'Reembolsos'}</h2>}

      {/* Abas internas — ocultas quando gerenciadas pelo componente pai */}
      {!semTabs && (
        <div style={s.abas}>
          <button style={{...s.aba, ...(isAnotacoes ? s.abaAtiva : {})}} onClick={() => { setAba('anotacoes'); setForm(null); }}>
            📝 Anotações <span style={s.abaBadge}>{anotacoesFiltradas.length}</span>
          </button>
          <button style={{...s.aba, ...(!isAnotacoes ? s.abaAtiva : {})}} onClick={() => { setAba('reembolsos'); setForm(null); }}>
            🔄 Reembolsos <span style={s.abaBadge}>{reembolsosFiltrados.length}</span>
          </button>
        </div>
      )}

      {/* ─── ABA ANOTAÇÕES ─────────────────────────────────────────────────── */}
      {isAnotacoes && (
        <>
          {/* Formulário de anotação — aparece em qualquer nível */}
          {form && (
            <div style={s.formCard}>
              <h3 style={s.formTitulo}>{editId ? '✏️ Editar Anotação' : '📝 Nova Anotação'}</h3>
              <div style={s.formRow}>
                <div style={s.campo}>
                  <label style={s.label}>Cliente</label>
                  {form._clienteFixo ? (
                    <div style={{...s.input, background:'#ffffff', color:'#5f5874'}}>
                      {clientes.find(c => String(c.id) === String(form.clienteId))?.nome || (form.clienteId === '' ? 'Sem cliente' : '—')}
                    </div>
                  ) : (
                    <select style={s.input} value={form.clienteId} onChange={e => setForm({...form, clienteId:e.target.value})}>
                      <option value="">Selecione</option>
                      {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                  )}
                </div>
                <div style={s.campo}>
                  <label style={s.label}>Data</label>
                  <input type="date" style={s.input} value={form.data} onChange={e => setForm({...form, data:e.target.value})} />
                </div>
              </div>
              <div style={s.campo}>
                <label style={s.label}>Título *</label>
                <input type="text" style={s.input} value={form.titulo} placeholder="Resumo da anotação" onChange={e => setForm({...form, titulo:e.target.value})} />
              </div>
              {isAdmin && (
                <div style={s.campo}>
                  <label style={s.label}>Profissional</label>
                  <input type="text" style={s.input} value={form.profissional||''} placeholder="Nome do profissional" onChange={e => setForm({...form, profissional:e.target.value})} />
                </div>
              )}
              <div style={s.campo}>
                <label style={s.label}>Conteúdo</label>
                <textarea style={{...s.input, resize:'vertical', minHeight:90}} value={form.conteudo} placeholder="Detalhes..." onChange={e => setForm({...form, conteudo:e.target.value})} rows={4} />
              </div>
              <div style={s.campo}>
                <label style={s.label}>Anexos (PDF / Documento)</label>
                <div style={s.anexoRow}>
                  <label style={s.btnAnexar}>
                    <Paperclip size={14}/> Adicionar arquivo(s)
                    <input type="file" accept=".pdf,.doc,.docx,.png,.jpg" multiple style={{display:'none'}} onChange={anexarArquivo} />
                  </label>
                </div>
                {(form.anexos||[]).length > 0 && (
                  <div style={s.anexoLista}>
                    {(form.anexos||[]).map((a, i) => (
                      <span key={i} style={s.anexoChip}>
                        <FileText size={12}/> {a.nome}
                        <button style={s.btnRemAnexo} onClick={() => removerAnexo(i)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={s.formActions}>
                <button style={s.btnSalvar} onClick={salvarAnotacao}>Salvar</button>
                <button style={s.btnCancelar} onClick={() => { setForm(null); setEditId(null); }}>Cancelar</button>
              </div>
            </div>
          )}

          {/* NÍVEL 1 — Lista de pastas */}
          {pastaClienteId === null && !form && (() => {
            // Agrupa anotações por clienteId
            const anotacoesBase2 = isAdmin ? anotacoes : anotacoes.filter(a => !a.profissional || a.profissional === profissionalAtual);
            const porCliente = {};
            anotacoesBase2.forEach(a => {
              const chave = a.clienteId ? String(a.clienteId) : 'sem-cliente';
              if (!porCliente[chave]) porCliente[chave] = [];
              porCliente[chave].push(a);
            });
            const entradas = Object.entries(porCliente).sort((a, b) => {
              if (a[0] === 'sem-cliente') return 1;
              if (b[0] === 'sem-cliente') return -1;
              const ca = clientes.find(c => String(c.id) === a[0])?.nome || '';
              const cb = clientes.find(c => String(c.id) === b[0])?.nome || '';
              return ca.localeCompare(cb);
            });
            return (
              <>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.75rem 0'}}>
                  <span style={{color:'#5f5874', fontSize:13}}>{entradas.length} pasta(s) de anotações</span>
                  {!form && <button style={s.btnNovo} onClick={() => novaAnotacao(undefined)}><Plus size={16}/> Nova Anotação</button>}
                </div>
                {entradas.length === 0
                  ? <div style={s.vazio}><Folder size={36} color="#e5e0ef"/><p>Nenhuma anotação registrada.</p></div>
                  : <div className="mobile-grid-1" style={s.pastasGrid}>
                      {entradas.map(([chave, lista]) => {
                        const cliente = chave === 'sem-cliente' ? null : clientes.find(c => String(c.id) === chave);
                        const nomePasta = cliente ? cliente.nome : 'Sem cliente';
                        const datas = lista.map(a => a.data).filter(Boolean).sort();
                        const ultimaData = datas.length ? new Date(datas[datas.length-1]+'T00:00').toLocaleDateString('pt-BR') : '—';
                        return (
                          <div key={chave} style={s.pastaCard} onClick={() => setPastaClienteId(chave)}>
                            <div style={s.pastaIcone}>📁</div>
                            <div style={s.pastaNome}>{nomePasta}</div>
                            <div style={s.pastaMeta}>{lista.length} anotação(ões)</div>
                            <div style={s.pastaMeta}>Última: {ultimaData}</div>
                          </div>
                        );
                      })}
                    </div>
                }
              </>
            );
          })()}

          {/* NÍVEL 2 — Anotações do cliente selecionado */}
          {pastaClienteId !== null && !form && (() => {
            const anotacoesBase2 = isAdmin ? anotacoes : anotacoes.filter(a => !a.profissional || a.profissional === profissionalAtual);
            const clienteSel = pastaClienteId === 'sem-cliente' ? null : clientes.find(c => String(c.id) === pastaClienteId);
            const nomePasta = clienteSel ? clienteSel.nome : 'Sem cliente';
            const lista = anotacoesBase2
              .filter(a => pastaClienteId === 'sem-cliente' ? !a.clienteId : String(a.clienteId) === pastaClienteId)
              .sort((a, b) => (b.data || '').localeCompare(a.data || ''));
            return (
              <>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.5rem 0 0.75rem'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'0.75rem'}}>
                    <button style={s.btnVoltar} onClick={() => { setPastaClienteId(null); setForm(null); }}>← Voltar</button>
                    <span style={{fontSize:'1.1rem', fontWeight:700, color:'#1c1530'}}>📁 {nomePasta}</span>
                  </div>
                  {!form && (
                    <button style={s.btnNovo} onClick={() => novaAnotacao(pastaClienteId === 'sem-cliente' ? '' : pastaClienteId)}>
                      <Plus size={16}/> Nova Anotação
                    </button>
                  )}
                </div>
                <div style={s.lista}>
                  {lista.length === 0
                    ? <div style={s.vazio}><FileText size={36} color="#e5e0ef"/><p>Nenhuma anotação nesta pasta.</p></div>
                    : lista.map(an => {
                        const cliente = clientes.find(c => String(c.id) === String(an.clienteId));
                        return (
                          <div key={an.id} style={s.card}>
                            <div style={s.cardHeader}>
                              <div>
                                <div style={s.cardTitulo}>{an.titulo}</div>
                                <div style={s.cardMeta}>{an.data ? new Date(an.data+'T00:00').toLocaleDateString('pt-BR') : '—'}{an.profissional ? ` · 👤 ${an.profissional}` : ''}</div>
                              </div>
                              <div style={s.acoes}>
                                <button style={s.btnExport} onClick={() => exportarPDF(htmlAnotacao(an, cliente?.nome), an.titulo || 'Anotação')} title="Exportar PDF">PDF</button>
                                <button style={s.btnExport} onClick={() => exportarWord(htmlAnotacao(an, cliente?.nome), an.titulo || 'Anotação')} title="Exportar Word">DOC</button>
                                <button style={s.btnEdit} onClick={() => editarAnotacao(an)}><Edit2 size={14}/></button>
                                <button style={s.btnDel} onClick={() => deletarAnotacao(an.id)}><Trash2 size={14}/></button>
                              </div>
                            </div>
                            {an.conteudo && <p style={s.cardConteudo}>{an.conteudo}</p>}
                            {(an.anexos||[]).length > 0 && (
                              <div style={s.anexoLista}>
                                {(an.anexos||[]).map((a, i) => (
                                  <button key={i} style={s.anexoChipDown} onClick={() => baixarAnexo(a)} title={a.nome}>
                                    <Download size={12}/> {a.nome}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                  }
                </div>
              </>
            );
          })()}
        </>
      )}

      {/* ─── ABA REEMBOLSOS ────────────────────────────────────────────────── */}
      {!isAnotacoes && (
        <>
          {/* Cards resumo — clicáveis pra filtrar por status (clicar de novo limpa) */}
          <div className="mobile-grid-1" style={s.statusGrid}>
            {[['PENDENTE','#f59e0b'],['PAGO','#10b981'],['RECUSADO','#ef4444']].map(([st, cor]) => {
              const ativo = filtroStatus === st;
              return (
                <div
                  key={st}
                  onClick={() => setFiltroStatus(ativo ? '' : st)}
                  title={ativo ? 'Clique pra limpar o filtro' : `Filtrar por ${st}`}
                  style={{
                    ...s.statusCard,
                    cursor:'pointer',
                    borderColor: ativo ? cor : '#e5e0ef',
                    boxShadow: ativo ? `0 0 0 1px ${cor}` : 'none',
                  }}
                >
                  <span style={{...s.statusBadge, color:cor, borderColor:cor}}>{st}{ativo ? ' ✓' : ''}</span>
                  <div style={{...s.statusValor, color:cor}}>R$ {totalPorStatus(st).toLocaleString('pt-BR',{minimumFractionDigits:2, maximumFractionDigits:2})}</div>
                  <div style={s.statusCount}>{countPorStatus(st)} item(ns)</div>
                </div>
              );
            })}
          </div>

          <div style={s.toolbar}>
            <div style={s.filtroGrupo}>
              <label style={s.filtroLabel}>Filtrar por Cliente</label>
              <select style={s.select} value={filtroReembolsos} onChange={e => setFiltroReembolsos(e.target.value)}>
                <option value="">Todos os Reembolsos</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <label style={s.filtroLabel}>Filtrar por Status</label>
              <select style={s.select} value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
                <option value="">Todos os Status</option>
                <option value="PENDENTE">PENDENTE</option>
                <option value="PAGO">PAGO</option>
                <option value="RECUSADO">RECUSADO</option>
              </select>
            </div>
            <div style={{display:'flex', gap:'0.5rem', flexWrap:'wrap', alignItems:'center'}}>
              {!form && reembolsosFiltrados.length > 0 && (
                <>
                  <button style={s.btnRelatorio} onClick={() => exportarPDF(htmlRelatorioReembolsos(reembolsosFiltrados, clientes), 'Relatório de Reembolsos')} title="Gerar relatório PDF de todos">
                    <FileText size={15}/> Relatório PDF
                  </button>
                  <button style={{...s.btnRelatorio, color:'#3b82f6', borderColor:'#3b82f6'}} onClick={() => exportarWord(htmlRelatorioReembolsos(reembolsosFiltrados, clientes), 'Relatório de Reembolsos')} title="Gerar relatório Word de todos">
                    <FileText size={15}/> Relatório DOC
                  </button>
                </>
              )}
              {!form && selecionados.length > 0 && (
                <>
                  <span style={{fontSize:12, color:'#5f5874'}}>{selecionados.length} selecionado(s)</span>
                  <button style={{...s.btnRelatorio, color:'#d97706', borderColor:'#d97706'}} onClick={abrirNotaDebito}>
                    <FileText size={15}/> Emitir Nota de Débito
                  </button>
                </>
              )}
              {!form && notasVisiveis.length > 0 && (
                <button style={{...s.btnRelatorio, color:'#5f5874', borderColor:'#e5e0ef'}} onClick={() => setVerHistoricoNotas(v => !v)}>
                  <Folder size={15}/> Histórico ({notasVisiveis.length})
                </button>
              )}
              {!form && <button style={s.btnNovo} onClick={novoReembolso}><Plus size={16}/> Novo Reembolso</button>}
            </div>
          </div>

          {verHistoricoNotas && notasVisiveis.length > 0 && (
            <div style={{...s.tabelaCard, marginBottom:'1rem'}}>
              <div style={{padding:'0.875rem 1rem', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid #e5e0ef'}}>
                <span style={{fontWeight:700, color:'#1c1530', fontSize:14}}>Histórico de Notas de Débito</span>
              </div>
              <table style={s.tabela}>
                <thead><tr>
                  <th style={s.th}>Número</th>
                  <th style={s.th}>Emissão</th>
                  <th style={s.th}>Cliente</th>
                  <th style={s.th}>Vencimento</th>
                  <th style={s.th}>Total</th>
                  <th style={s.th}></th>
                </tr></thead>
                <tbody>
                  {[...notasVisiveis].reverse().map(n => (
                    <tr key={n.id}>
                      <td style={{...s.td, fontWeight:600, color:'#d97706'}}>{n.numero}</td>
                      <td style={s.td}>{n.dataEmissao ? new Date(n.dataEmissao+'T00:00').toLocaleDateString('pt-BR') : '—'}</td>
                      <td style={s.td}>{n.cliente}</td>
                      <td style={s.td}>{n.vencimento ? new Date(n.vencimento+'T00:00').toLocaleDateString('pt-BR') : '—'}</td>
                      <td style={{...s.td, fontWeight:600}}>R$ {n.total?.toLocaleString('pt-BR',{minimumFractionDigits:2, maximumFractionDigits:2})}</td>
                      <td style={{...s.td, whiteSpace:'nowrap'}}>
                        <button style={s.btnEdit} onClick={() => abrirEdicaoNota(n)} title="Editar"><Edit2 size={14}/></button>
                        <button style={{...s.btnEdit, color:'#d97706'}} onClick={() => imprimirNota(n,'pdf')} title="PDF"><FileText size={14}/></button>
                        <button style={{...s.btnEdit, color:'#3b82f6'}} onClick={() => imprimirNota(n,'word')} title="DOC"><FileOutput size={14}/></button>
                        <button style={s.btnDel} onClick={() => { if(window.confirm('Remover esta nota do histórico?')) setNotasDebito(notasDebito.filter(x => x.id !== n.id)); }} title="Remover"><Trash2 size={14}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {form && (
            <div style={s.formCard}>
              <h3 style={s.formTitulo}>{editId ? '✏️ Editar Reembolso' : '🔄 Novo Reembolso'}</h3>
              <div style={s.formRow}>
                <div style={s.campo}>
                  <label style={s.label}>Cliente</label>
                  <select style={s.input} value={form.clienteId} onChange={e => setForm({...form, clienteId:e.target.value})}>
                    <option value="">Selecione</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>
                <div style={s.campo}>
                  <label style={s.label}>Data</label>
                  <input type="date" style={s.input} value={form.data} onChange={e => setForm({...form, data:e.target.value})} />
                </div>
              </div>
              <div style={s.campo}>
                  <label style={s.label}>Categoria</label>
                  <select style={s.input} value={form.categoria||''} onChange={e => setForm({...form, categoria:e.target.value})}>
                    <option value="">Selecione</option>
                    {CATEGORIAS_REEMBOLSO.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
              </div>
              <div style={s.campo}>
                <label style={s.label}>Projeto / Referência</label>
                <input type="text" style={s.input} value={form.projeto||''} placeholder="Ex: Projeto X – Contrato 001" onChange={e => setForm({...form, projeto:e.target.value})} />
              </div>
              <div style={s.campo}>
                <label style={s.label}>Descrição *</label>
                <input type="text" style={s.input} value={form.descricao} placeholder="Ex: Passagem aérea São Paulo → Rio" onChange={e => setForm({...form, descricao:e.target.value})} />
              </div>
              <div style={s.formRow}>
                <div style={s.campo}>
                  <label style={s.label}>Valor (R$) *</label>
                  <input type="number" style={s.input} value={form.valor} placeholder="0,00" step="0.01" onChange={e => setForm({...form, valor:e.target.value})} />
                </div>
                <div style={s.campo}>
                  <label style={s.label}>Status</label>
                  <select style={s.input} value={form.status} onChange={e => setForm({...form, status:e.target.value})}>
                    <option value="PENDENTE">Pendente</option>
                    <option value="PAGO">Pago</option>
                    <option value="RECUSADO">Recusado</option>
                  </select>
                </div>
              </div>
              <div style={s.campo}>
                <label style={s.label}>Comprovante (Nº NF, link, etc.)</label>
                <input type="text" style={s.input} value={form.comprovante||''} placeholder="Ref. nota fiscal, link..." onChange={e => setForm({...form, comprovante:e.target.value})} />
              </div>
              {isAdmin && (
                <div style={s.campo}>
                  <label style={s.label}>Profissional</label>
                  <input type="text" style={s.input} value={form.profissional||''} placeholder="Nome do profissional" onChange={e => setForm({...form, profissional:e.target.value})} />
                </div>
              )}
              <div style={s.campo}>
                <label style={s.label}>Anexos (PDF / Comprovante)</label>
                <div style={s.anexoRow}>
                  <label style={s.btnAnexar}>
                    <Paperclip size={14}/> Adicionar arquivo(s)
                    <input type="file" accept=".pdf,.doc,.docx,.png,.jpg" multiple style={{display:'none'}} onChange={anexarArquivo} />
                  </label>
                </div>
                {(form.anexos||[]).length > 0 && (
                  <div style={s.anexoLista}>
                    {(form.anexos||[]).map((a, i) => (
                      <span key={i} style={s.anexoChip}>
                        <FileText size={12}/> {a.nome}
                        <button style={s.btnRemAnexo} onClick={() => removerAnexo(i)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={s.formActions}>
                {editId && <button style={s.btnExcluir} onClick={() => { deletarReembolso(editId); setForm(null); setEditId(null); }}>Excluir</button>}
                <button style={s.btnSalvar} onClick={salvarReembolso}>Salvar</button>
                <button style={s.btnCancelar} onClick={() => { setForm(null); setEditId(null); }}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Tabela — visível apenas em desktop */}
          <div className="reembolsos-desktop-table" style={s.tabelaCard}>
            <table style={s.tabela}>
              <thead>
                <tr>
                  <th style={s.th}><input type="checkbox" checked={reembolsosFiltrados.length > 0 && selecionados.length === reembolsosFiltrados.length} onChange={toggleTodos} title="Selecionar todos" /></th>
                  {['Data','Cliente','Descrição','Valor','Status','Anexo','Ações'].map(h =>
                    <th key={h} style={s.th}>{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {reembolsosFiltrados.length === 0
                  ? <tr><td colSpan={8} style={s.vazio2}>Nenhum reembolso registrado.</td></tr>
                  : reembolsosFiltrados.map(r => {
                      const cliente = clientes.find(c => String(c.id) === String(r.clienteId));
                      const st = r.status || 'PENDENTE';
                      const cor = STATUS_COR[st] || '#5f5874';
                      const marcado = selecionados.includes(r.id);
                      return (
                        <tr key={r.id} style={{...s.tr, background: marcado ? 'rgba(124,58,237,0.08)' : undefined}}>
                          <td style={s.td}><input type="checkbox" checked={marcado} onChange={() => toggleSelecionado(r.id)} /></td>
                          <td style={s.td}>{r.data ? new Date(r.data+'T00:00').toLocaleDateString('pt-BR') : '—'}</td>
                          <td style={s.td}>{cliente?.nome || '—'}</td>
                          <td style={s.td}>{r.descricao}</td>
                          <td style={{...s.td, color:'#10b981', fontWeight:600}}>R$ {parseFloat(r.valor||0).toLocaleString('pt-BR',{minimumFractionDigits:2, maximumFractionDigits:2})}</td>
                          <td style={s.td}><span style={{...s.badge, color:cor, borderColor:cor}}>{st}</span></td>
                          <td style={s.td}>
                            {(r.anexos||[]).length > 0
                              ? <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                                  {(r.anexos||[]).map((a,i) => (
                                    <button key={i} style={s.btnAnexoDown} onClick={() => baixarAnexo(a)} title={a.nome}>
                                      <Download size={12}/> <span style={{fontSize:11,maxWidth:100,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.nome}</span>
                                    </button>
                                  ))}
                                </div>
                              : <span style={{color:'#5f5874',fontSize:12}}>—</span>
                            }
                          </td>
                          <td style={s.td}>
                            <div style={s.acoes}>
                              <button style={s.btnEdit} onClick={() => editarReembolso(r)}><Edit2 size={14}/></button>
                              <button style={s.btnDel} onClick={() => deletarReembolso(r.id)}><Trash2 size={14}/></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                }
              </tbody>
            </table>
          </div>

          {/* Toolbar de seleção — só mobile */}
          {reembolsosFiltrados.length > 0 && (
            <div className="reembolsos-mobile-toolbar" style={s.toolbarMobile}>
              <label style={s.reembolsoCheckLabel}>
                <input type="checkbox" checked={selecionados.length === reembolsosFiltrados.length} onChange={toggleTodos} style={{ width:18, height:18, accentColor:'#7c3aed' }} />
                <span style={{ fontSize:13, color:'#1c1530', fontWeight:600 }}>
                  {selecionados.length === 0
                    ? 'Selecionar todos'
                    : selecionados.length === reembolsosFiltrados.length
                      ? `Todos os ${reembolsosFiltrados.length} selecionados`
                      : `${selecionados.length} selecionado(s)`}
                </span>
              </label>
              {selecionados.length > 0 && (
                <button style={s.btnLimparSel} onClick={() => setSelecionados([])}>Limpar</button>
              )}
            </div>
          )}

          {/* Cards — visíveis apenas em mobile */}
          <div className="reembolsos-mobile-cards">
            {reembolsosFiltrados.length === 0 && (
              <div style={s.vazio2}>Nenhum reembolso registrado.</div>
            )}
            {reembolsosFiltrados.map(r => {
              const cliente = clientes.find(c => String(c.id) === String(r.clienteId));
              const st = r.status || 'PENDENTE';
              const cor = STATUS_COR[st] || '#5f5874';
              const marcado = selecionados.includes(r.id);
              return (
                <div key={r.id} style={{...s.reembolsoCard, ...(marcado ? s.reembolsoCardSel : {})}}>
                  <div style={s.reembolsoCardHead}>
                    <label style={s.reembolsoCheckLabel}>
                      <input type="checkbox" checked={marcado} onChange={() => toggleSelecionado(r.id)} style={{ width:18, height:18, accentColor:'#7c3aed' }} />
                      <span style={s.reembolsoData}>{r.data ? new Date(r.data+'T00:00').toLocaleDateString('pt-BR') : '—'}</span>
                    </label>
                    <span style={{ ...s.badge, color:cor, borderColor:cor, fontSize:11 }}>{st}</span>
                  </div>
                  <div style={s.reembolsoDescricao}>{r.descricao || '—'}</div>
                  <div style={s.reembolsoMeta}>
                    {cliente?.nome && <span>👤 {cliente.nome}</span>}
                    {r.categoria && <span>· {r.categoria}</span>}
                    {r.projeto && <span>· {r.projeto}</span>}
                  </div>
                  <div style={s.reembolsoValor}>
                    R$ {parseFloat(r.valor||0).toLocaleString('pt-BR',{minimumFractionDigits:2, maximumFractionDigits:2})}
                  </div>
                  {(r.anexos||[]).length > 0 && (
                    <div style={s.reembolsoAnexos}>
                      {(r.anexos||[]).map((a,i) => (
                        <button key={i} style={s.btnAnexoDown} onClick={() => baixarAnexo(a)} title={a.nome}>
                          <Download size={12}/> <span style={{ fontSize:11, maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.nome}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={s.reembolsoAcoes}>
                    <button style={s.btnReembolsoAcao} onClick={() => editarReembolso(r)}><Edit2 size={14}/> Editar</button>
                    <button style={{ ...s.btnReembolsoAcao, color:'#ef4444', borderColor:'rgba(239,68,68,0.4)' }} onClick={() => deletarReembolso(r.id)}><Trash2 size={14}/> Excluir</button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ─── MODAL NOTA DE DÉBITO ──────────────────────────────────────────── */}
      {notaDebitoModal && (
        <div style={sm.overlay} onClick={() => { setNotaDebitoModal(false); setEditandoNota(null); }}>
          <div style={sm.modal} onClick={e => e.stopPropagation()}>
            <div style={sm.header}>
              <div>
                <h3 style={sm.titulo}>{editandoNota ? '✏️ Editar Nota de Débito' : '📄 Emitir Nota de Débito'}</h3>
                <p style={sm.subtitulo}>{editandoNota
                  ? `Nota ${notaForm.numero}`
                  : `${reembolsosSelecionados.length} reembolso(s) selecionado(s) · R$ ${reembolsosSelecionados.reduce((s,r)=>s+parseFloat(r.valor||0),0).toLocaleString('pt-BR',{minimumFractionDigits:2, maximumFractionDigits:2})}`
                }</p>
              </div>
              <button style={sm.btnFechar} onClick={() => { setNotaDebitoModal(false); setEditandoNota(null); }}>✕</button>
            </div>

            <div style={sm.separador} />

            <div style={sm.campo}>
              <label style={sm.label}>Número da Nota</label>
              <input type="text" style={sm.input} value={notaForm.numero} onChange={e => setNotaForm({...notaForm, numero:e.target.value})} placeholder="Ex: ND-2026-00001" />
            </div>

            <div style={sm.campo}>
              <label style={sm.label}>Meio de Pagamento</label>
              <select style={sm.input} value={notaForm.meioPagamento} onChange={e => setNotaForm({...notaForm, meioPagamento:e.target.value, dadosPagamento: e.target.value === 'Dinheiro' ? '' : notaForm.dadosPagamento})}>
                <option value="">Selecione</option>
                <option value="PIX">PIX</option>
                <option value="Transferência Bancária (TED/DOC)">Transferência Bancária (TED/DOC)</option>
                <option value="Boleto">Boleto</option>
                <option value="Cheque">Cheque</option>
                <option value="Dinheiro">Dinheiro</option>
              </select>
            </div>

            {notaForm.meioPagamento && notaForm.meioPagamento !== 'Dinheiro' && (
              <div style={sm.campo}>
                <label style={sm.label}>Dados para Pagamento</label>
                <input type="text" style={sm.input} value={notaForm.dadosPagamento} placeholder="Ex: PIX: 11.222.333/0001-44  |  Banco Itaú Ag 1234 Cc 56789-0" onChange={e => setNotaForm({...notaForm, dadosPagamento:e.target.value})} />
              </div>
            )}

            <div style={sm.campo}>
              <label style={sm.label}>Prazo para Pagamento</label>
              <input type="date" style={sm.input} value={notaForm.vencimento} onChange={e => setNotaForm({...notaForm, vencimento:e.target.value})} />
            </div>

            <div style={sm.campo}>
              <label style={sm.label}>Observações</label>
              <textarea style={{...sm.input, resize:'vertical', minHeight:80}} value={notaForm.observacoes} placeholder="Informações adicionais que devem constar na nota..." rows={3} onChange={e => setNotaForm({...notaForm, observacoes:e.target.value})} />
            </div>

            <div style={sm.separador} />

            <div style={sm.footer}>
              <button style={sm.btnCancelar} onClick={() => { setNotaDebitoModal(false); setEditandoNota(null); }}>Cancelar</button>
              <button style={sm.btnWord} onClick={() => gerarNotaDebito('word')}><FileText size={15}/> Gerar DOC</button>
              <button style={sm.btnPdf} onClick={() => gerarNotaDebito('pdf')}><FileText size={15}/> Gerar PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  container:    { display:'flex', flexDirection:'column', gap:'1.25rem' },
  titulo:       { fontSize:'1.8rem', fontWeight:700, color:'#1c1530' },
  abas:         { display:'flex', gap:'0.5rem', borderBottom:'1px solid #e5e0ef', paddingBottom:'0.75rem' },
  aba:          { background:'transparent', border:'1px solid #e5e0ef', borderRadius:20, color:'#5f5874', padding:'7px 18px', fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:6 },
  abaAtiva:     { background:'#7c3aed', border: '1px solid #7c3aed', color:'white' },
  abaBadge:     { background:'rgba(255,255,255,0.2)', borderRadius:10, padding:'1px 7px', fontSize:11 },
  toolbar:      { display:'flex', justifyContent:'space-between', alignItems:'flex-end', flexWrap:'wrap', gap:'1rem', background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.25rem' },
  filtroGrupo:  { display:'flex', flexDirection:'column', gap:4 },
  filtroLabel:  { fontSize:12, color:'#5f5874', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' },
  select:       { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, color:'#1c1530', padding:'8px 12px', fontSize:13, minWidth:200 },
  btnNovo:      { display:'flex', alignItems:'center', gap:6, background:'#7c3aed', color:'white', border:'none', borderRadius:20, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' },
  btnRelatorio: { display:'flex', alignItems:'center', gap:5, background:'transparent', border:'1px solid #7c3aed', borderRadius:20, color:'#6d28d9', padding:'8px 16px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' },
  formCard:     { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.5rem', display:'flex', flexDirection:'column', gap:'1rem' },
  formTitulo:   { fontSize:'1.1rem', fontWeight:700, color:'#1c1530', margin:0 },
  formRow:      { display:'flex', gap:'1rem', flexWrap:'wrap' },
  campo:        { display:'flex', flexDirection:'column', gap:5, flex:1, minWidth:140 },
  label:        { fontSize:12, fontWeight:600, color:'#5f5874', textTransform:'uppercase', letterSpacing:'0.04em' },
  input:        { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, color:'#1c1530', padding:'8px 12px', fontSize:13, outline:'none', width:'100%', boxSizing:'border-box' },
  anexoRow:     { display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' },
  btnAnexar:    { display:'flex', alignItems:'center', gap:6, background:'transparent', border:'1px dashed #e5e0ef', borderRadius:8, color:'#5f5874', padding:'8px 14px', fontSize:12, cursor:'pointer' },
  anexoNome:    { display:'flex', alignItems:'center', gap:4, fontSize:12, color:'#8b5cf6', background:'rgba(139,92,246,0.1)', borderRadius:6, padding:'4px 10px' },
  formActions:  { display:'flex', gap:'0.75rem', justifyContent:'flex-end' },
  btnSalvar:    { background:'#7c3aed', border:'none', borderRadius:8, color:'white', cursor:'pointer', padding:'9px 22px', fontSize:13, fontWeight:600 },
  btnCancelar:  { background:'transparent', border:'1px solid #e5e0ef', borderRadius:8, color:'#5f5874', cursor:'pointer', padding:'9px 16px', fontSize:13 },
  btnExcluir:   { background:'transparent', border:'1px solid #ef4444', borderRadius:8, color:'#ef4444', cursor:'pointer', padding:'9px 16px', fontSize:13, marginRight:'auto' },
  lista:        { display:'flex', flexDirection:'column', gap:'0.75rem' },
  card:         { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.1rem 1.25rem', display:'flex', flexDirection:'column', gap:'0.5rem' },
  cardHeader:   { display:'flex', justifyContent:'space-between', alignItems:'flex-start' },
  cardTitulo:   { fontSize:15, fontWeight:600, color:'#1c1530' },
  cardMeta:     { fontSize:12, color:'#5f5874', marginTop:2 },
  cardConteudo: { fontSize:13, color:'#2d2640', lineHeight:1.6, marginTop:'0.25rem', whiteSpace:'pre-wrap' },
  acoes:        { display:'flex', gap:4 },
  btnEdit:      { background:'transparent', border:'none', color:'#8b5cf6', cursor:'pointer', padding:4, display:'flex' },
  btnDel:       { background:'transparent', border:'none', color:'#ef4444', cursor:'pointer', padding:4, display:'flex' },
  btnAnexoDown:  { background:'transparent', border:'1px solid #e5e0ef', borderRadius:6, color:'#8b5cf6', cursor:'pointer', padding:'4px 8px', display:'flex', alignItems:'center', gap:4 },
  btnExport:     { background:'transparent', border:'1px solid #e5e0ef', borderRadius:6, color:'#5f5874', cursor:'pointer', padding:'3px 7px', fontSize:11, fontWeight:600 },
  anexoLista:    { display:'flex', flexWrap:'wrap', gap:6, marginTop:4 },
  anexoChip:     { display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#8b5cf6', background:'rgba(139,92,246,0.1)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:20, padding:'3px 10px' },
  btnRemAnexo:   { background:'transparent', border:'none', color:'#8b5cf6', cursor:'pointer', fontSize:14, lineHeight:1, padding:'0 2px', fontWeight:700 },
  anexoChipDown: { display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#8b5cf6', background:'rgba(139,92,246,0.1)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:20, padding:'3px 10px', cursor:'pointer' },
  vazio:        { display:'flex', flexDirection:'column', alignItems:'center', gap:'0.75rem', padding:'3rem', color:'#5f5874', fontSize:14 },
  statusGrid:   { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'1rem' },
  statusCard:   { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.25rem', display:'flex', flexDirection:'column', gap:'0.5rem' },
  statusBadge:  { fontSize:11, fontWeight:700, border:'1.5px solid', borderRadius:20, padding:'2px 10px', alignSelf:'flex-start', letterSpacing:'0.05em' },
  statusValor:  { fontSize:'1.5rem', fontWeight:700 },
  statusCount:  { fontSize:12, color:'#5f5874' },
  tabelaCard:   { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, overflow:'hidden' },
  tabela:       { width:'100%', borderCollapse:'collapse' },
  th:           { padding:'0.875rem 1rem', textAlign:'left', fontSize:12, fontWeight:600, color:'#5f5874', textTransform:'uppercase', letterSpacing:'0.05em', background:'#f7f5fb', borderBottom:'1px solid #e5e0ef' },
  tr:           {},
  td:           { padding:'0.875rem 1rem', fontSize:13, color:'#1c1530', borderBottom:'1px solid #eeeaf5' },
  badge:        { fontSize:11, fontWeight:700, border:'1.5px solid', borderRadius:20, padding:'2px 10px' },
  vazio2:       { padding:'2rem', textAlign:'center', color:'#5f5874', fontSize:13 },
  pastasGrid:   { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:'1rem' },
  // Cards mobile de reembolso
  reembolsoCard:    { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'0.875rem 1rem', display:'flex', flexDirection:'column', gap:'0.5rem' },
  reembolsoCardSel: { borderColor:'#7c3aed', background:'rgba(124,58,237,0.08)' },
  reembolsoCardHead:{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'0.5rem' },
  reembolsoCheckLabel:{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer' },
  reembolsoData:    { fontSize:12, color:'#5f5874', fontWeight:600 },
  reembolsoDescricao:{ fontSize:14, fontWeight:600, color:'#1c1530', lineHeight:1.4 },
  reembolsoMeta:    { display:'flex', flexWrap:'wrap', gap:'0.4rem', fontSize:12, color:'#5f5874' },
  reembolsoValor:   { fontSize:18, fontWeight:700, color:'#10b981' },
  reembolsoAnexos:  { display:'flex', flexWrap:'wrap', gap:'0.4rem' },
  reembolsoAcoes:   { display:'flex', gap:'0.5rem', flexWrap:'wrap', borderTop:'1px solid #eeeaf5', paddingTop:'0.5rem' },
  btnReembolsoAcao: { display:'flex', alignItems:'center', gap:5, background:'transparent', border:'1px solid #e5e0ef', borderRadius:6, color:'#8b5cf6', cursor:'pointer', padding:'6px 12px', fontSize:12, fontWeight:600 },
  toolbarMobile:    { display:'none', justifyContent:'space-between', alignItems:'center', background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:10, padding:'0.6rem 0.875rem' },
  btnLimparSel:     { background:'transparent', border:'1px solid #e5e0ef', borderRadius:6, color:'#5f5874', cursor:'pointer', padding:'4px 10px', fontSize:11, fontWeight:600 },
  pastaCard:    { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.25rem', display:'flex', flexDirection:'column', gap:'0.4rem', cursor:'pointer', transition:'border-color 0.15s' },
  pastaIcone:   { fontSize:'2rem', lineHeight:1 },
  pastaNome:    { fontSize:15, fontWeight:700, color:'#1c1530', marginTop:4 },
  pastaMeta:    { fontSize:12, color:'#5f5874' },
  btnVoltar:    { background:'transparent', border:'1px solid #e5e0ef', borderRadius:8, color:'#5f5874', cursor:'pointer', padding:'7px 14px', fontSize:13, fontWeight:600 },
};

const sm = {
  overlay:    { position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:'1rem' },
  modal:      { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:16, padding:'1.75rem', width:'100%', maxWidth:520, display:'flex', flexDirection:'column', gap:'1rem', maxHeight:'90vh', overflowY:'auto' },
  header:     { display:'flex', justifyContent:'space-between', alignItems:'flex-start' },
  titulo:     { fontSize:18, fontWeight:700, color:'#1c1530', margin:'0 0 4px' },
  subtitulo:  { fontSize:13, color:'#5f5874', margin:0 },
  btnFechar:  { background:'transparent', border:'none', color:'#5f5874', cursor:'pointer', fontSize:18, lineHeight:1, padding:4, flexShrink:0 },
  separador:  { borderTop:'1px solid #e5e0ef', margin:'0.25rem 0' },
  campo:      { display:'flex', flexDirection:'column', gap:5 },
  label:      { fontSize:12, fontWeight:600, color:'#5f5874', textTransform:'uppercase', letterSpacing:'0.04em' },
  input:      { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, color:'#1c1530', padding:'9px 12px', fontSize:14, outline:'none', width:'100%', boxSizing:'border-box' },
  footer:     { display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'0.25rem' },
  btnCancelar:{ background:'transparent', border:'1px solid #e5e0ef', borderRadius:8, color:'#5f5874', cursor:'pointer', padding:'9px 16px', fontSize:13 },
  btnWord:    { display:'flex', alignItems:'center', gap:6, background:'transparent', border:'1px solid #3b82f6', borderRadius:8, color:'#3b82f6', cursor:'pointer', padding:'9px 18px', fontSize:13, fontWeight:600 },
  btnPdf:     { display:'flex', alignItems:'center', gap:6, background:'#d97706', border:'none', borderRadius:8, color:'white', cursor:'pointer', padding:'9px 18px', fontSize:13, fontWeight:600 },
};
